
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';

interface AddStaticEntryFormProps {
  onAddEntry: (entry: {
    url: string;
    priority: number;
    change_frequency: string;
    entry_type: string;
  }) => Promise<void>;
  isLoading: boolean;
}

export const AddStaticEntryForm: React.FC<AddStaticEntryFormProps> = ({
  onAddEntry,
  isLoading,
}) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formData, setFormData] = useState({
    url: '',
    priority: 0.5,
    change_frequency: 'weekly',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.url.trim()) {
      toast.error('URL is required');
      return;
    }

    if (!formData.url.startsWith('/')) {
      toast.error('URL must start with /');
      return;
    }

    try {
      await onAddEntry({
        url: formData.url.trim(),
        priority: formData.priority,
        change_frequency: formData.change_frequency,
        entry_type: 'static',
      });

      // Reset form
      setFormData({
        url: '',
        priority: 0.5,
        change_frequency: 'weekly',
      });
      setIsFormVisible(false);
      
      toast.success('Static URL added successfully');
    } catch (error) {
      console.error('Error adding static entry:', error);
      toast.error('Failed to add static URL');
    }
  };

  const handleCancel = () => {
    setFormData({
      url: '',
      priority: 0.5,
      change_frequency: 'weekly',
    });
    setIsFormVisible(false);
  };

  if (!isFormVisible) {
    return (
      <Button
        onClick={() => setIsFormVisible(true)}
        className="flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        Add Static URL
      </Button>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Add Static Sitemap Entry
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="url">URL Path *</Label>
              <Input
                id="url"
                type="text"
                placeholder="/about"
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                required
              />
              <p className="text-xs text-muted-foreground">
                Must start with / (e.g., /about, /contact)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Input
                id="priority"
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseFloat(e.target.value) || 0.5 }))}
              />
              <p className="text-xs text-muted-foreground">
                0.0 to 1.0 (higher = more important)
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="change_frequency">Change Frequency</Label>
              <Select 
                value={formData.change_frequency} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, change_frequency: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">Always</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="never">Never</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
            >
              {isLoading ? 'Adding...' : 'Add Static URL'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
